import React, { useContext, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

import { GlobalContext } from 'providers/GlobalProvider';
import { BulkVerificationRunView } from 'api/bitedb/entities/bulkVerificationRun.service';
import { BulkVerificationRunTracker } from './useBulkVerificationRun';
import {
  processedCount,
  remainingCount,
  retryableFailedIds,
  runCredits,
  startedByName,
} from './bulkVerificationRunProgress';
import {
  formatCount,
  plural,
  validDate,
  widthPercent,
} from './bulkVerificationFormat';
import BulkVerificationRunFailures from './BulkVerificationRunFailures';

const Meter: React.FC<{ run: BulkVerificationRunView; running?: boolean }> = ({
  run,
  running,
}) => (
  <div
    className={`bdb-bulk-run-meter${running ? ' is-running' : ''}`}
    aria-hidden='true'
  >
    <i
      className='bdb-bulk-run-meter-ok'
      style={{ width: widthPercent(run.completed, run.total) }}
    />
    <i
      className='bdb-bulk-run-meter-fail'
      style={{ width: widthPercent(run.failed, run.total) }}
    />
    <i
      className='bdb-bulk-run-meter-skip'
      style={{ width: widthPercent(run.skipped, run.total) }}
    />
  </div>
);

const Counts: React.FC<{ run: BulkVerificationRunView; running: boolean }> = ({
  run,
  running,
}) => (
  <div className='bdb-bulk-run-counts'>
    <span>
      <span className='bdb-bulk-run-swatch bdb-bulk-run-meter-ok' />
      <b>{formatCount(run.completed)}</b> verified
    </span>
    <span>
      <span className='bdb-bulk-run-swatch bdb-bulk-run-meter-fail' />
      <b>{formatCount(run.failed)}</b> failed
    </span>
    <span>
      <span className='bdb-bulk-run-swatch bdb-bulk-run-meter-skip' />
      <b>{formatCount(run.skipped)}</b> skipped
    </span>
    {running && (
      <span>
        <span className='bdb-bulk-run-swatch bdb-bulk-run-swatch-togo' />
        <b>{formatCount(remainingCount(run))}</b> to go
      </span>
    )}
  </div>
);

const POLL_FAILING_COPY = "Can't reach the run's progress right now. Retrying…";

/** Progress, then the completion summary, of the database's Bulk Verification Run, above the entity table. */
const BulkVerificationRunStrip: React.FC<{
  tracker: BulkVerificationRunTracker;
}> = ({ tracker }) => {
  const { cur_user } = useContext(GlobalContext);
  const [failuresOpen, setFailuresOpen] = useState(false);
  const { run } = tracker;
  if (!run) {
    if (!tracker.isPollFailing) return null;
    return (
      <section
        className='bdb-bulk-run-strip is-warn'
        aria-label='Business verification'
      >
        <div className='bdb-bulk-run-top'>
          <div className='bdb-bulk-run-icon is-warn' aria-hidden='true'>
            <WarningAmberOutlinedIcon sx={{ fontSize: 18 }} />
          </div>
          <div>
            <div className='bdb-bulk-run-title'>Business verification</div>
            <div className='bdb-bulk-run-meta' aria-live='polite'>
              {POLL_FAILING_COPY}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (tracker.isRunning) {
    const startedAt = validDate(run.createdAt);
    const starter = startedByName(
      run.requestedBy,
      cur_user?.basics?.id,
      cur_user?.selected_team?.members,
    );
    return (
      <section
        className={`bdb-bulk-run-strip${
          tracker.isPollFailing ? ' is-warn' : ''
        }`}
        aria-label='Business verification'
      >
        <div className='bdb-bulk-run-top'>
          <div
            className={`bdb-bulk-run-icon ${
              tracker.isPollFailing ? 'is-warn' : 'is-running'
            }`}
            aria-hidden='true'
          >
            {tracker.isPollFailing ? (
              <WarningAmberOutlinedIcon sx={{ fontSize: 18 }} />
            ) : (
              <VerifiedUserOutlinedIcon sx={{ fontSize: 18 }} />
            )}
          </div>
          <div>
            <div className='bdb-bulk-run-title'>
              Business verification
              <span className='bdb-bulk-run-title-count'>
                {' '}
                · {formatCount(processedCount(run))} of {formatCount(run.total)}
              </span>
            </div>
            <div className='bdb-bulk-run-meta' aria-live='polite'>
              {tracker.isPollFailing
                ? `${POLL_FAILING_COPY} The run itself keeps going.`
                : `Started by ${starter}${
                    startedAt
                      ? `, ${formatDistanceToNow(startedAt, {
                          addSuffix: true,
                        })}`
                      : ''
                  }. Runs in the background.`}
            </div>
          </div>
        </div>
        <Meter run={run} running={!tracker.isPollFailing} />
        <Counts run={run} running />
      </section>
    );
  }

  const finishedAt = validDate(run.completedAt);
  const credits = runCredits(run);
  const retryIds = retryableFailedIds(run);
  return (
    <section
      className='bdb-bulk-run-strip is-done'
      aria-label='Business verification'
    >
      <div className='bdb-bulk-run-top'>
        <div className='bdb-bulk-run-icon is-done' aria-hidden='true'>
          <CheckOutlinedIcon sx={{ fontSize: 18 }} />
        </div>
        <div>
          <div className='bdb-bulk-run-title'>
            Business verification finished
            <span className='bdb-bulk-run-title-count'>
              {' '}
              · {formatCount(run.completed)} of {formatCount(run.total)}{' '}
              verified
            </span>
          </div>
          <div className='bdb-bulk-run-meta'>
            {finishedAt
              ? `Finished ${format(finishedAt, 'd MMM, HH:mm')}. `
              : ''}
            {formatCount(credits.used)}{' '}
            {plural(credits.used, 'credit', 'credits')} used
            {credits.returned > 0
              ? `, ${formatCount(credits.returned)} returned`
              : ''}
            .
          </div>
        </div>
        <div className='bdb-bulk-run-actions'>
          {run.failed > 0 && (
            <button
              type='button'
              className='bdb-bulk-run-link'
              aria-expanded={failuresOpen}
              onClick={() => setFailuresOpen((open) => !open)}
            >
              {failuresOpen
                ? 'Hide failures'
                : `View ${formatCount(run.failed)} failed`}
            </button>
          )}
          {retryIds.length > 0 && (
            <button
              type='button'
              className='btn-outline bdb-bulk-run-retry'
              onClick={() => tracker.retryFailed(retryIds)}
            >
              Retry {formatCount(retryIds.length)} failed
            </button>
          )}
          <button
            type='button'
            className='btn-outline bdb-bulk-run-dismiss'
            onClick={tracker.dismiss}
          >
            Dismiss
          </button>
        </div>
      </div>
      <Meter run={run} />
      <Counts run={run} running={false} />
      {failuresOpen && <BulkVerificationRunFailures run={run} />}
    </section>
  );
};

export default BulkVerificationRunStrip;
